import {Children} from 'react';

export const PROJECT_PHOTOS_MOBILE_SLIDE_PCT = 80;
export const PROJECT_PHOTOS_DESKTOP_SLIDE_PCT = 33.4;

function cssTranslate(position, axis = 'horizontal') {
  const value = `${position}%`;
  const transform = axis === 'horizontal'
    ? `translate3d(${value},0,0)`
    : `translate3d(0,${value},0)`;

  return {
    WebkitTransform: transform,
    msTransform: transform,
    OTransform: transform,
    transform,
  };
}

function slidePercent(props) {
  if(props.centerMode && props.axis !== 'vertical'){
    return Number(props.centerSlidePercentage) || 100;
  }
  return 100;
}

export function isProjectPhotosWrap(previousItem, selectedItem, lastIndex) {
  // Need at least 3 slides so first↔last is not also an adjacent step.
  if(lastIndex < 2) return false;
  return (previousItem === 0 && selectedItem === lastIndex)
    || (previousItem === lastIndex && selectedItem === 0);
}

/**
 * Loop clones sit next to the real first/last slides. Dragging first→last uses
 * the prepended last clone (index -1); last→first uses the appended first clone.
 */
export function getProjectPhotoWrapCloneIndex(previousItem, selectedItem, lastIndex) {
  if(previousItem === 0 && selectedItem === lastIndex) return -1;
  if(previousItem === lastIndex && selectedItem === 0) return lastIndex + 1;
  return selectedItem;
}

/**
 * Track offset for a photo slide. `start` keeps the active image on the left
 * (mobile peek). `center` matches desktop three-up. infiniteLoop clones the
 * last slide in front, so real index 0 sits at -slidePct.
 */
export function getProjectPhotoPosition(index, {infiniteLoop, slidePct, align} = {}) {
  let positionIndex = index;
  if(infiniteLoop) positionIndex += 1;

  let position = -positionIndex * slidePct;
  if(align === 'center'){
    position += (100 - slidePct) / 2;
  }
  return position;
}

function transitionStyle(props, instant) {
  const duration = instant ? '0ms' : `${props.transitionTime || 350}ms`;
  return {
    WebkitTransitionDuration: duration,
    MozTransitionDuration: duration,
    OTransitionDuration: duration,
    msTransitionDuration: duration,
    transitionDuration: duration,
  };
}

function trackStyle(index, props, align, instant) {
  const position = getProjectPhotoPosition(index, {
    infiniteLoop: props.infiniteLoop,
    slidePct: slidePercent(props),
    align,
  });

  return {
    ...cssTranslate(position, props.axis),
    ...transitionStyle(props, instant),
  };
}

function wrapDisplayIndex(props, state) {
  const lastIndex = Children.count(props.children) - 1;
  const wrapping = props.infiniteLoop && isProjectPhotosWrap(state.previousItem, state.selectedItem, lastIndex);
  return {
    lastIndex,
    wrapping,
    displayIndex: wrapping
      ? getProjectPhotoWrapCloneIndex(state.previousItem, state.selectedItem, lastIndex)
      : state.selectedItem,
  };
}

export function createProjectPhotosWrapSnapper(applySnap) {
  let timer = null;
  let pendingStyle = null;

  const snapper = {
    schedule(style, ms) {
      pendingStyle = style;
      if(timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        timer = null;
        const next = pendingStyle;
        pendingStyle = null;
        if(next) applySnap(next);
      }, ms);
    },
    flush() {
      if(!pendingStyle) return;
      if(timer) window.clearTimeout(timer);
      timer = null;
      const next = pendingStyle;
      pendingStyle = null;
      applySnap(next);
    },
    cancel() {
      if(timer) window.clearTimeout(timer);
      timer = null;
      pendingStyle = null;
    },
    isPending() {
      return pendingStyle != null;
    },
  };

  return snapper;
}

function scheduleWrapSnap(props, state, align, snapper) {
  if(!snapper || state.swiping) return;
  snapper.schedule(
    trackStyle(state.selectedItem, props, align, true),
    props.transitionTime || 350,
  );
}

export function projectPhotosAnimationHandler(props, state, align, snapper) {
  const {wrapping, displayIndex} = wrapDisplayIndex(props, state);
  const itemListStyle = {
    ...cssTranslate(getProjectPhotoPosition(displayIndex, {
      infiniteLoop: props.infiniteLoop,
      slidePct: slidePercent(props),
      align,
    }), props.axis),
  };

  if(!state.swiping){
    Object.assign(itemListStyle, transitionStyle(props, false));
    if(wrapping) scheduleWrapSnap(props, state, align, snapper);
  }

  return {itemListStyle};
}

export function projectPhotosSwipeAnimationHandler(delta, props, state, setState, align, snapper) {
  if(snapper) snapper.flush();

  const isHorizontal = props.axis !== 'vertical';
  const slidePct = slidePercent(props);
  const currentPosition = getProjectPhotoPosition(state.selectedItem, {
    infiniteLoop: props.infiniteLoop,
    slidePct,
    align,
  });
  const axisDelta = isHorizontal ? delta.x : delta.y;
  const itemSize = state.itemSize || 1;
  const position = currentPosition + (axisDelta / itemSize) * slidePct;
  const hasMoved = Math.abs(axisDelta) > (props.swipeScrollTolerance || 5);

  if(!props.preventMovementUntilSwipeScrollTolerance || hasMoved || state.swipeMovementStarted){
    if(!state.swipeMovementStarted){
      setState({swipeMovementStarted: true});
    }
    return {itemListStyle: cssTranslate(position, props.axis)};
  }

  return {};
}

export function projectPhotosStopSwipingHandler(props, state, align, snapper) {
  const {wrapping, displayIndex} = wrapDisplayIndex(props, state);
  if(wrapping) scheduleWrapSnap(props, state, align, snapper);

  return {
    itemListStyle: trackStyle(displayIndex, props, align, false),
  };
}
